import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  Card,
  Modal,
  ModalProps,
  useAccordionToggle,
} from "react-bootstrap";
import AccordionContext from "react-bootstrap/AccordionContext";
import { usePlayersDispatch, usePlayersState } from "store/hooks";
import {
  PlayerParticipationType,
  PlayersExcludedByPeriodType,
} from "store/types";
import { POSITION } from "config/constants";
import "./ExcludePlayersModal.scss";

type ExcludePlayersModalProps = ModalProps & {
  onClose: () => void;
  onApply: (updatedExcludedPlayers: PlayersExcludedByPeriodType[]) => void;
  players: PlayerParticipationType[];
  periodId: number;
  periodName: string;
};

const ExcludePlayersModal: React.FC<ExcludePlayersModalProps> = ({
  onClose,
  onApply,
  players,
  periodId,
  periodName,
  ...props
}) => {
  const { excludedPlayersByPeriod } = usePlayersState();
  const { setPlayersExcludedByPeriod } = usePlayersDispatch();
  const [searchTerm, setSearchTerm] = useState("");
  const [excludedMap, setExcludedMap] = useState<Record<string, boolean>>({});

  const getExcludedPlayersForSession = useCallback(
    (
      currentExcluded: PlayersExcludedByPeriodType[] = [],
      targetSessionId: number
    ) => {
      const next: Record<string, boolean> = {};
      currentExcluded.forEach((entry) => {
        Object.entries(entry || {}).forEach(([playerId, sessions]) => {
          if (!sessions) {
            return;
          }
          const hasSession = sessions.some(
            (session) => Number(session) === Number(targetSessionId)
          );
          if (hasSession) {
            next[String(playerId)] = true;
          }
        });
      });
      return next;
    },
    []
  );

  useEffect(() => {
    setExcludedMap(
      getExcludedPlayersForSession(excludedPlayersByPeriod || [], periodId)
    );
  }, [excludedPlayersByPeriod, periodId, getExcludedPlayersForSession]);

  const positionUnitOrder = ["OFF", "DEF", "SPEC"];
  const positionGroupsByUnit = POSITION.types.reduce<Record<string, string[]>>(
    (acc, { name, value: { unit } }) => {
      if (!acc[unit]) {
        acc[unit] = [];
      }
      acc[unit].push(name);
      return acc;
    },
    {}
  );
  const unitLabels: Record<string, string> = {
    OFF: "OFFENSE",
    DEF: "DEFENSE",
    SPEC: "SPECIAL TEAMS",
  };

  const playersByGroup = useMemo(() => {
    const groups: Record<string, PlayerParticipationType[]> = {};
    players.forEach((player) => {
      const groupKey = player.positionGroup || player.position;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(player);
    });
    Object.keys(groups).forEach((key) => {
      groups[key].sort(
        (a, b) => parseInt(a.jerseyNumber, 10) - parseInt(b.jerseyNumber, 10)
      );
    });
    return groups;
  }, [players]);

  const searchValue = searchTerm.trim().toLowerCase();
  const filteredPlayersByGroup = useMemo(() => {
    if (!searchValue) {
      return playersByGroup;
    }
    const filtered: Record<string, PlayerParticipationType[]> = {};
    Object.entries(playersByGroup).forEach(([groupKey, groupPlayers]) => {
      const matched = groupPlayers.filter((player) =>
        player.name.toLowerCase().includes(searchValue)
      );
      if (matched.length) {
        filtered[groupKey] = matched;
      }
    });
    return filtered;
  }, [playersByGroup, searchValue]);

  const allPlayerIds = useMemo(() => {
    const ids = new Set<string>();
    players.forEach((player) => {
      if (player.nflId) {
        ids.add(String(player.nflId));
      }
    });
    return Array.from(ids);
  }, [players]);

  const unitDefinitions = useMemo(() => {
    const knownPositionGroups = POSITION.types.map((type) => type.name);
    const extraPositionGroup = Object.keys(playersByGroup).filter(
      (positionGroup) => !knownPositionGroups.includes(positionGroup)
    );
    return positionUnitOrder.map((unit) => ({
      key: unit,
      label: unitLabels[unit] || unit,
      groups:
        unit === "SPEC"
          ? [...positionGroupsByUnit.SPEC, ...extraPositionGroup]
          : positionGroupsByUnit[unit] || [],
    }));
  }, [playersByGroup]);

  const togglePlayers = useCallback(
    (playerIds: string[], shouldExclude: boolean) => {
      setExcludedMap((prev) => {
        const next = { ...prev };
        playerIds.forEach((id) => {
          if (shouldExclude) {
            next[id] = true;
          } else {
            delete next[id];
          }
        });
        return next;
      });
    },
    []
  );

  const getIncludedCheckboxState = useCallback(
    (playerIds: string[]) => {
      if (!playerIds.length) {
        return { checked: false, indeterminate: false };
      }
      let excludedCount = 0;
      playerIds.forEach((id) => {
        if (excludedMap[id]) {
          excludedCount += 1;
        }
      });
      return {
        checked: excludedCount === 0,
        indeterminate: excludedCount > 0 && excludedCount < playerIds.length,
      };
    },
    [excludedMap]
  );

  const handleToggleAllPlayers = useCallback(() => {
    const includedState = getIncludedCheckboxState(allPlayerIds);
    togglePlayers(allPlayerIds, includedState.checked);
  }, [allPlayerIds, getIncludedCheckboxState, togglePlayers]);

  const handleTogglePlayer = useCallback(
    (playerId: string) => {
      togglePlayers([playerId], !excludedMap[playerId]);
    },
    [excludedMap, togglePlayers]
  );

  const getIdsForGroup = useCallback(
    (groupKey: string) =>
      (playersByGroup[groupKey] || []).map((player) => String(player.nflId)),
    [playersByGroup]
  );

  const getIdsForUnit = useCallback(
    (unitKey: string) => {
      const unitDefinition = unitDefinitions.find(
        (unit) => unit.key === unitKey
      );
      if (!unitDefinition) {
        return [] as string[];
      }
      return unitDefinition.groups.flatMap((groupKey) =>
        getIdsForGroup(groupKey)
      );
    },
    [unitDefinitions, getIdsForGroup]
  );

  const handleToggleGroup = useCallback(
    (groupKey: string) => {
      const ids = getIdsForGroup(groupKey);
      if (!ids.length) {
        return;
      }
      const { checked } = getIncludedCheckboxState(ids);
      togglePlayers(ids, checked);
    },
    [getIdsForGroup, getIncludedCheckboxState, togglePlayers]
  );

  const handleToggleUnit = useCallback(
    (unitKey: string) => {
      const ids = getIdsForUnit(unitKey);
      if (!ids.length) {
        return;
      }
      const { checked } = getIncludedCheckboxState(ids);
      togglePlayers(ids, checked);
    },
    [getIdsForUnit, getIncludedCheckboxState, togglePlayers]
  );

  const handleApply = useCallback(() => {
    const updatedMap: Record<string, Set<number>> = {};
    (excludedPlayersByPeriod || []).forEach(
      (entry: PlayersExcludedByPeriodType) => {
        Object.entries(entry || {}).forEach(([playerId, sessions]) => {
          const id = String(playerId);
          if (!updatedMap[id]) {
            updatedMap[id] = new Set<number>();
          }
          (sessions || []).forEach((session) => {
            const sessionNumber = Number(session);
            if (!Number.isNaN(sessionNumber)) {
              updatedMap[id].add(sessionNumber);
            }
          });
        });
      }
    );

    allPlayerIds.forEach((playerId) => {
      if (!updatedMap[playerId]) {
        updatedMap[playerId] = new Set<number>();
      }
      if (excludedMap[playerId]) {
        updatedMap[playerId].add(Number(periodId));
      } else {
        updatedMap[playerId].delete(Number(periodId));
      }
      if (updatedMap[playerId].size === 0) {
        delete updatedMap[playerId];
      }
    });

    const updatedExcludedPlayers = Object.entries(updatedMap).map(
      ([playerId, sessions]) => ({
        [playerId]: Array.from(sessions).sort((a, b) => a - b),
      })
    );

    onApply(updatedExcludedPlayers);
    onClose();
  }, [
    excludedPlayersByPeriod,
    excludedMap,
    allPlayerIds,
    periodId,
    setPlayersExcludedByPeriod,
    onClose,
  ]);

  const allCheckboxState = getIncludedCheckboxState(allPlayerIds);
  const excludedCount = useMemo(
    () => allPlayerIds.filter((id) => excludedMap[id]).length,
    [allPlayerIds, excludedMap]
  );

  const RowToggle = ({
    eventKey,
    className,
    children,
  }: {
    eventKey: string;
    className?: string;
    children: React.ReactNode;
  }) => {
    const currentEventKey = useContext(AccordionContext);
    const decoratedOnClick = useAccordionToggle(eventKey);
    const isCurrentEventKey = currentEventKey === eventKey;

    return (
      <div
        className={`${className || ""} exclude-players-modal-row-toggle`}
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("input")) {
            return;
          }
          decoratedOnClick(event);
        }}
      >
        {children}
        <div>
          <i
            className={`mdi ${
              isCurrentEventKey ? "mdi-chevron-up" : "mdi-chevron-down"
            }`}
          />
        </div>
      </div>
    );
  };

  const Checkbox = ({
    id,
    checked,
    indeterminate,
    onChange,
  }: {
    id: string;
    checked: boolean;
    indeterminate?: boolean;
    onChange: () => void;
  }) => {
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = Boolean(indeterminate);
      }
    }, [indeterminate]);

    return (
      <>
        <input
          ref={inputRef}
          id={id}
          type="checkbox"
          className="styled-checkbox"
          checked={checked}
          onChange={(event) => {
            event.stopPropagation();
            onChange();
          }}
          onClick={(event) => event.stopPropagation()}
        />
        <label htmlFor={id} onClick={(event) => event.stopPropagation()} />
      </>
    );
  };

  return (
    <Modal
      {...props}
      animation={false}
      onHide={onClose}
      dialogClassName={"exclude-players-modal"}
      contentClassName={"exclude-players-modal-content"}
    >
      <Modal.Header
        closeButton={true}
        className={"exclude-players-modal-header"}
      >
        <div className={"title"}>Exclude Players</div>
        <div className={"session"}>{`- ${periodName} -`}</div>
      </Modal.Header>
      <Modal.Body>
        <div className={"exclude-players-modal-search"}>
          <input
            type="text"
            placeholder="Search by player name"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <i className="mdi mdi-magnify" style={{ color: "#AABDCB" }}></i>
        </div>
        <div className={"exclude-players-modal-summary"}>
          <div>Players list ({players.length})</div>
          <div>Players Excluded ({excludedCount})</div>
        </div>
        <div className={"exclude-players-modal-table"}>
          <div className={"exclude-players-modal-table-header"}>
            <div>
              <Checkbox
                id="exclude-all-players"
                checked={allCheckboxState.checked}
                indeterminate={allCheckboxState.indeterminate}
                onChange={handleToggleAllPlayers}
              />
            </div>
            <div>Position</div>
            <div>#</div>
            <div>Player name</div>
            <></>
          </div>
          <div className={"exclude-players-modal-table-list"}>
            {unitDefinitions.map((unit) => {
              const unitPlayerIds = getIdsForUnit(unit.key);
              const unitCheckboxState = getIncludedCheckboxState(unitPlayerIds);
              const unitHasDisplayGroups = unit.groups.some(
                (groupKey) =>
                  (filteredPlayersByGroup[groupKey] || []).length > 0
              );
              if (!unitHasDisplayGroups) {
                return null;
              }
              return (
                <Accordion key={unit.key} defaultActiveKey="0">
                  <Card className={"exclude-players-modal-card"}>
                    <Card.Header className="exclude-players-modal-card-header">
                      <RowToggle
                        eventKey="0"
                        className={"exclude-players-modal-unit"}
                      >
                        <div>
                          <Checkbox
                            id={`exclude-unit-${unit.key}`}
                            checked={unitCheckboxState.checked}
                            indeterminate={unitCheckboxState.indeterminate}
                            onChange={() => handleToggleUnit(unit.key)}
                          />
                        </div>
                        <div>{unit.label}</div>
                      </RowToggle>
                    </Card.Header>
                    <Accordion.Collapse eventKey="0">
                      <Card.Body className={"exclude-players-modal-card-body"}>
                        {unit.groups.map((groupKey) => {
                          const groupPlayersDisplay =
                            filteredPlayersByGroup[groupKey] || [];
                          if (!groupPlayersDisplay.length) {
                            return null;
                          }
                          const groupPlayerIds = getIdsForGroup(groupKey);
                          const groupCheckboxState =
                            getIncludedCheckboxState(groupPlayerIds);
                          return (
                            <Accordion
                              key={`${unit.key}-${groupKey}`}
                              defaultActiveKey="0"
                            >
                              <Card className={"exclude-players-modal-card"}>
                                <Card.Header className="exclude-players-modal-card-header">
                                  <RowToggle
                                    eventKey="0"
                                    className={"exclude-players-modal-group"}
                                  >
                                    <div>
                                      <Checkbox
                                        id={`exclude-group-${groupKey}`}
                                        checked={groupCheckboxState.checked}
                                        indeterminate={
                                          groupCheckboxState.indeterminate
                                        }
                                        onChange={() =>
                                          handleToggleGroup(groupKey)
                                        }
                                      />
                                    </div>
                                    <div>{groupKey}</div>
                                  </RowToggle>
                                </Card.Header>
                                <Accordion.Collapse eventKey="0">
                                  <Card.Body
                                    className={
                                      "exclude-players-modal-card-body"
                                    }
                                  >
                                    {groupPlayersDisplay.map((player) => {
                                      const playerId = String(player.nflId);
                                      const isExcluded = Boolean(
                                        excludedMap[playerId]
                                      );
                                      return (
                                        <div
                                          key={playerId}
                                          className={
                                            "exclude-players-modal-player-row" +
                                            (isExcluded
                                              ? " exclude-players-modal-player-row-excluded"
                                              : "")
                                          }
                                        >
                                          <div>
                                            <Checkbox
                                              id={`exclude-player-${playerId}`}
                                              checked={!isExcluded}
                                              onChange={() =>
                                                handleTogglePlayer(playerId)
                                              }
                                            />
                                          </div>
                                          <div>{player.jerseyNumber}</div>
                                          <div>{player.name}</div>
                                          <div>
                                            {isExcluded && (
                                              <i className="mdi mdi-account-cancel-outline exclude-players-modal__excluded-icon" />
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </Card.Body>
                                </Accordion.Collapse>
                              </Card>
                            </Accordion>
                          );
                        })}
                      </Card.Body>
                    </Accordion.Collapse>
                  </Card>
                </Accordion>
              );
            })}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="text-right footer-s">
        <button className="btn-transparet" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-blue" onClick={handleApply}>
          Apply
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExcludePlayersModal;
